import { AccessTime } from "@mui/icons-material";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { AwakeningRecord } from "../observationNotes";

interface AwakeningRecordsBlockProps {
  records: AwakeningRecord[];
}

/** Linha do tempo compacta exibida no final da seção Sono. */
export function AwakeningRecordsBlock({
  records,
}: AwakeningRecordsBlockProps) {
  if (records.length === 0) return null;

  const countLabel = `${records.length} ${
    records.length === 1 ? "registro" : "registros"
  }`;

  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2.5 },
        py: 2,
        minWidth: 0,
        borderTop: "1px solid",
        borderColor: "neutral200.main",
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5, minWidth: 0 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
          <AccessTime
            sx={{ color: "primary700.main", fontSize: 21, flexShrink: 0 }}
            aria-hidden
          />
          <Typography
            sx={{
              fontSize: "1.0625rem",
              fontWeight: 700,
              overflowWrap: "anywhere",
            }}
          >
            Registros de despertar
          </Typography>
        </Stack>

        <Chip
          label={countLabel}
          size="small"
          sx={{
            flexShrink: 0,
            fontWeight: 700,
            color: "primary800.main",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          }}
        />
      </Stack>

      <Box
        sx={{
          display: "grid",
          gap: 1.25,
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {records.map((record) => (
          <Stack
            key={record.id}
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{
              p: 1.5,
              minWidth: 0,
              border: "1px solid",
              borderColor: "neutral200.main",
              borderRadius: 2,
              bgcolor: "background.paper",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                borderRadius: "50%",
                color: "primary700.main",
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              }}
            >
              <AccessTime sx={{ fontSize: 20 }} aria-hidden />
            </Box>

            <Stack spacing={0.15} sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {record.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.0625rem",
                  fontWeight: 700,
                  lineHeight: 1.35,
                  overflowWrap: "anywhere",
                }}
              >
                {record.value}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Box>
    </Box>
  );
}
